import type { TManagerCompanyBulkUsersStats } from '~/modules/manager/types';
import type { TManagerCompanyUsersBulkActionStatuses } from '~/modules/manager/company/users/types';
import { EManagerCompanyUsersBulkAction } from '~/modules/manager/company/users/types';
import { ESubscriptionType } from '~/modules/manager/types';

// Models
import { ManagerCompanyModel } from '~/modules/manager/models/company.model';

export const useManagerCompanyUsersBulkActions = (companyId: number) => {
  const companyModel: ManagerCompanyModel = new ManagerCompanyModel();

  const stats: Ref<TManagerCompanyBulkUsersStats | null> =
    ref<TManagerCompanyBulkUsersStats | null>(null);

  const statuses: ComputedRef<TManagerCompanyUsersBulkActionStatuses> =
    computed<TManagerCompanyUsersBulkActionStatuses>(() => {
      const total: number = stats.value?.total ?? 0;
      const active: number = stats.value?.active ?? 0;
      const subscribedSales: number = stats.value?.subscriptions['user-sale-opportunity'] ?? 0;
      const subscribedLettings: number = stats.value?.subscriptions['user-let-opportunity'] ?? 0;

      return {
        [EManagerCompanyUsersBulkAction.ENABLE]: total - active > 0,
        [EManagerCompanyUsersBulkAction.DISABLE]: active > 0,
        [EManagerCompanyUsersBulkAction.SUBSCRIBE_SALES]: total - subscribedSales > 0,
        [EManagerCompanyUsersBulkAction.UNSUBSCRIBE_SALES]: subscribedSales > 0,
        [EManagerCompanyUsersBulkAction.SUBSCRIBE_LETTINGS]: total - subscribedLettings > 0,
        [EManagerCompanyUsersBulkAction.UNSUBSCRIBE_LETTINGS]: subscribedLettings > 0,
      };
    });

  const setStats = (value: TManagerCompanyBulkUsersStats) => {
    stats.value = value;
  };

  const handleEnableAll = () => companyModel.bulkActivateUsers(companyId);

  const handleDisableAll = () => companyModel.bulkSuspendUsers(companyId);

  const handleSubscribeAll = (type: ESubscriptionType) =>
    companyModel.bulkSubscribeUsers(companyId, { type });

  const handleUnsubscribeAll = (type: ESubscriptionType) =>
    companyModel.bulkUnsubscribeUsers(companyId, { type });

  const fetchStats = async () => {
    try {
      const response: TManagerCompanyBulkUsersStats = await companyModel.bulkUserStats(companyId);

      setStats(response);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data:', error);
    }
  };

  fetchStats();

  return {
    statuses,
    fetchStats,
    handleEnableAll,
    handleDisableAll,
    handleSubscribeAll,
    handleUnsubscribeAll,
  };
};
